import { createSlice } from "@reduxjs/toolkit";
import { sortAllView } from "../utils";

const defaultColumnForShipmentV1 = [
  "SHIPMENT NO",
  "SHIPMENT TYPE",
  "CREATE DATE",
  "STATUS",
  "FREIGHT FORWARDER",
  "CUSTOMER",
  "FREIGHT TYPE",
  "TOTAL FREIGHT COST",
  "FROM FACILITY",
  "MOVEMENT TYPE",
  "PRIORITY",
  "INCOTERM",
];

const defaultColumnForDelivery = [
  "DELIVERY NO",
  "DELIVERY TYPE",
  "STATUS",
  "ORIGIN",
  "DESTINATION",
  "DISPATCH DATE",
  "SERVICE LEVEL",
  "PRIORITY",
  "CUSTOMER",
  "DANGEROUS GOODS",
  "CUSTOMS VALUE",
  "INSURANCE VALUE",
];

const defaultColumnForEquipmentType = [
  "NAME",
  "CODE",
  "CATEGORY",
  "DEFAULT",
  "CREATED BY",
  "CREATE DATE",
];
const defaultColumnForEquipment = [
  "EQUIPMENT TYPE",
  "NUMBER",
  "CREATED BY",
  "CREATE DATE",
  "EQUIPMENT STATUS",
];
const defaultColumnForShipmentLeg = [
  "SHIPMENT NAME",
  "SEQUENCE",
  "TYPE",
  "TRANSPORT MODE",
  "CARRIER",
  "VEHICLE / VESSEL",
  "ORIGIN",
  "DESTINATION",
  "DELIVER TO",
  "STATUS",
  "ETD",
  "ETA",
  "ATD",
  "ATA",
];

const defaultColumnForAsn = [
  "ASN NO",
  "SHIPMENT NO",
  "SUPPLIER",
  "FROM FACILITY",
  "TO FACILITY",
  "PURCHASE ORDERS",
  "TRANSFER ORDERS",
  "ORDER DATE",
  "ETA",
  "CREATED BY",
  "STATUS",
];

const initialState = {
  asn: null,
  asnList: { count: 0, results: [] },
  asnListParams: { limit: 10, offset: 0 },
  asnSelection: [],
  asnLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: defaultColumnForAsn,
      allViews: {
        "Default View": defaultColumnForAsn,
      },
      nameOfViews: [],
    },
  },

  consignment: null,
  consignmentBillsParams: { limit: 10, offset: 0 },
  consignmentFilesParams: { limit: 10, offset: 0 },
  consignments: { count: 0, results: [] },
  consignmentsParams: { limit: 10, offset: 0 },
  consignmentsSelection: [],
  consignmentsSelectionV2: [],

  consignmentItems: { count: 0, results: [] },
  consignmentItemsParams: { limit: 10, offset: 0 },
  consignmentItemsStatus: [],
  consignmentStatus: [],

  shipment: null,
  shipments: { count: 0, results: [] },
  shipmentsParams: { limit: 10, offset: 0 },

  shipmentV1: null,
  shipmentsV1: { count: 0, results: [] },
  shipmentsV1Params: { limit: 10, offset: 0 },
  shipmentsV1Selection: [],
  shipmentsV1Layout: {
    completeListOfViews: [],
    listView: {
      allItemsName: defaultColumnForShipmentV1,
      allViews: {
        "Default View": defaultColumnForShipmentV1,
      },
      nameOfViews: [],
    },
  },

  boardView: "false",
  allViews: {
    listView: {
      nameOfViews: [],
      allItemsName: [
        "SHIPMENT NAME",
        "ACTIONS",
        "SUPPLIER",
        "FROM FACILITY",
        "TO FACILITY",
        "ORDER DATE",
        "ETA",
        "CREATED BY",
        "STATUS",
      ],
      allViews: {
        "Default View": [
          "SHIPMENT NAME",
          "ACTIONS",
          "SUPPLIER",
          "FROM FACILITY",
          "TO FACILITY",
          "ORDER DATE",
          "ETA",
          "CREATED BY",
          "STATUS",
        ],
      },
    },
    boardView: {
      nameOfViews: [],
      allItemsName: [
        "Fully Received",
        "Seller Location",
        "Partially Received",
        "Cancelled",
        "Gated Out",
      ],
      allViews: {
        "Default View": ["Fully Received", "Seller Location", "Partially Received"],
      },
    },
    milestone: {
      milestoneList: [
        { name: "one", color: "red" },
        { name: "two", color: "sky" },
      ],
      milestoneNames: ["one", "two"],
      allItemsName: ["Uncategorized", "one", "two"],
    },
  },
  milestoneOrders: [],

  deliveries: { count: 0, results: [] },
  deliveryParams: { limit: 10, offset: 0 },
  delivery: null,
  deliverySelection: [],
  deliveryLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: defaultColumnForDelivery,
      allViews: {
        "Default View": defaultColumnForDelivery,
      },
      nameOfViews: [],
    },
  },

  equipmentTypeCategories: [],
  equipmentType: null,
  equipmentTypes: { count: 0, results: [] },
  equipmentTypesSelection: [],
  equipmentTypesParams: { limit: 10, offset: 0 },
  equipmentTypeLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: defaultColumnForEquipmentType,
      allViews: {
        "Default View": defaultColumnForEquipmentType,
      },
      nameOfViews: [],
    },
  },

  equipment: null,
  equipments: { count: 0, results: [] },
  equipmentsSelection: [],
  equipmentsParams: { limit: 10, offset: 0 },
  equipmentLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: defaultColumnForEquipment,
      allViews: {
        "Default View": defaultColumnForEquipment,
      },
      nameOfViews: [],
    },
  },

  shipmentLegs: { count: 0, results: [] },
  shipmentLegsParams: { limit: 10, offset: 0 },
  shipmentLeg: null,
  shipmentLegsLayout: {
    completeListOfViews: [],
    listView: {
      allItemsName: defaultColumnForShipmentLeg,
      allViews: {
        "Default View": defaultColumnForShipmentLeg,
      },
      nameOfViews: [],
    },
  },
  shipmentLegCarriersSelection: [],
  shipmentLegOriginsSelection: [],
  shipmentLegDestinationsSelection: [],
  shipmentLegDeliverTosSelection: [],
  shipmentLegVehicleReferencesSelection: [],
  shipmentLegSequencesSelection: [],
};

const shipmentSlice = createSlice({
  name: "shipment",
  initialState,
  reducers: {
    addConsignmentFiles: (state, action) => {
      state.consignment = {
        ...state.consignment,
        files: {
          count: (state.consignment?.files?.count || 0) + action.payload.length,
          results: [...action.payload, ...(state.consignment?.files?.results || [])],
        },
      };
    },
    addShipment: (state, action) => {
      state.shipments = {
        ...state.shipments,
        count: state.shipments.count + 1,
        results: [action.payload, ...state.shipments.results],
      };
    },
    addConsignmentNote: (state, action) => {
      if (!state.consignment) {
        state.consignment = {};
      }
      const notes = Array.isArray(state.consignment.notes) ? state.consignment.notes : [];
      state.consignment.notes = [...notes, action.payload];
    },
    updateConsignmentNote: (state, action) => {
      if (!state.consignment) {
        state.consignment = {};
      }
      const notes = Array.isArray(state.consignment.notes) ? state.consignment.notes : [];
      const index = notes.findIndex(note => note.id === action.payload.id);
      if (index !== -1) {
        notes[index] = { ...notes[index], ...action.payload };
      }
      state.consignment.notes = notes;
    },
    removeConsignmentNote: (state, action) => {
      if (!state.consignment) {
        state.consignment = {};
      }
      const notes = Array.isArray(state.consignment.notes) ? state.consignment.notes : [];
      state.consignment.notes = notes.filter(note => note.id !== action.payload);
    },
    confirmASN: (state, action) => {
      const { id } = action.payload;
      if (!state.asnList?.results) return;

      const index = state.asnList.results.findIndex(item => item.id === id);
      if (index !== -1) {
        state.asnList.results[index] = {
          ...state.asnList.results[index],
          status: "Confirmed",
        };
      }
    },
    editConsignment: (state, action) => {
      state.consignment = { ...state.consignment, ...action.payload };
    },
    editConsignmentItem: (state, action) => {
      action.payload.forEach(item => {
        const { id, ...rest } = item;
        const index = state.consignment.items.findIndex(obj => obj.id === id);
        if (index !== -1) {
          state.consignment.items[index] = { ...state.consignment.items[index], ...rest };
        }
      });
    },
    mutateConsignmentItem: (state, action) => {
      action.payload.forEach(item => {
        const index = state.consignment.items.findIndex(obj => obj.id === item.id);
        if (index !== -1) {
          state.consignment.items[index] = {
            ...state.consignment.items[index],
            received_qty: (
              Number(state.consignment.items[index].received_qty) + Number(item.received_qty)
            ).toFixed(4),
          };
        }
      });
      if (action.payload.length > 0) {
        state.consignment.status = action.payload[0].status;
      }
    },
    mutateShipmentLine: (state, action) => {
      action.payload.forEach(item => {
        const index = state.shipment.shipment_lines.findIndex(obj => obj.id === item.id);
        if (index !== -1) {
          state.shipment.shipment_lines[index] = {
            ...state.shipment.shipment_lines[index],
            received_qty: (
              Number(state.shipment.shipment_lines[index].received_qty) + Number(item.received_qty)
            ).toFixed(4),
          };
        }
      });
    },
    resetASN: state => {
      state.asn = initialState.asn;
    },
    resetASNList: state => {
      state.asnList = initialState.asnList;
    },
    resetASNListParams: state => {
      state.asnListParams = { limit: state.asnListParams.limit, offset: 0 };
    },
    resetConsignment: state => {
      state.consignment = initialState.consignment;
    },
    resetConsignmentItems: state => {
      state.consignmentItems = initialState.consignmentItems;
    },
    resetConsignmentItemsParams: state => {
      state.consignmentItemsParams = { limit: state.consignmentItemsParams.limit, offset: 0 };
    },
    resetConsignments: state => {
      state.consignments = initialState.consignments;
    },
    resetConsignmentsParams: state => {
      state.consignmentsParams = { limit: state.consignmentsParams.limit, offset: 0 };
    },
    resetConsignmentsSelection: (state, action) => {
      state.consignmentsSelection = initialState.consignmentsSelection;
    },
    resetShipment: state => {
      state.shipment = null;
    },
    resetShipments: state => {
      state.shipments = { count: 0, results: [] };
    },
    resetShipmentsParams: state => {
      state.shipmentsParams = { limit: state.shipmentsParams.limit, offset: 0 };
    },
    resetNotesList: state => {
      if (!state.consignment) {
        state.consignment = {};
      }
      state.consignment.notes = [];
    },
    setASN: (state, action) => {
      state.asn = action.payload;
    },
    setASNList: (state, action) => {
      state.asnList = action.payload;
    },
    setASNListParams: (state, action) => {
      state.asnListParams = action.payload;
    },
    updateASNListParams: (state, action) => {
      state.asnListParams = { ...state.asnListParams, ...action.payload };
    },
    setASNSelection: (state, action) => {
      state.asnSelection = action.payload;
    },
    setAsnLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.asnLayout = {
        ...state.asnLayout,
        listView: {
          ...state.asnLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.asnLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    clearASN: state => {
      state.asn = null;
    },
    setCancelConsignments: (state, action) => {
      const { ids } = action.payload;
      const idSet = new Set(ids);
      state.consignments.results.forEach(consignment => {
        if (idSet.has(consignment.id)) {
          consignment.status = "Cancelled";
        }
      });
    },
    /**
     * Updates the consignment entity in state, preserving sub-properties (bills, files,
     * notes) only when the incoming payload matches the current consignment id.
     *
     * @param {Object} state Slice state.
     * @param {{ payload: Object }} action Action with consignment payload.
     * @return {void}
     */
    setConsignment: (state, action) => {
      const isSameRecord =
        state.consignment?.id != null &&
        action.payload?.id != null &&
        String(state.consignment.id) === String(action.payload.id);
      state.consignment = {
        ...action.payload,
        bills: isSameRecord ? state.consignment.bills : undefined,
        files: isSameRecord ? state.consignment.files : undefined,
        notes: isSameRecord ? state.consignment.notes : undefined,
      };
    },
    setConsignmentBills: (state, action) => {
      if (!state.consignment) {
        state.consignment = {};
      }
      state.consignment.bills = { count: action.payload.count, results: action.payload.results };
    },
    setConsignmentBillsParams: (state, action) => {
      state.consignmentBillsParams = action.payload;
    },
    setConsignmentFiles: (state, action) => {
      // Initialize consignment if it doesn't exist
      if (!state.consignment) {
        state.consignment = {};
      }

      state.consignment.files = {
        count: action.payload.count,
        results: action.payload.results,
      };
    },
    setConsignmentFilesParams: (state, action) => {
      state.consignmentFilesParams = action.payload;
    },
    setConsignmentItems: (state, action) => {
      state.consignmentItems = action.payload;
    },
    setConsignmentItemsParams: (state, action) => {
      state.consignmentItemsParams = action.payload;
    },
    setConsignmentItemsStatus: (state, action) => {
      state.consignmentItemsStatus = action.payload;
    },
    setConsignmentStatus: (state, action) => {
      state.consignmentStatus = action.payload;
    },
    setConsignmentNotes: (state, action) => {
      if (!state.consignment) {
        state.consignment = {};
      }
      state.consignment.notes = action.payload;
    },
    setConsignments: (state, action) => {
      if (Array.isArray(action.payload)) {
        state.consignmentsSelection = action.payload;
      } else {
        state.consignments = action.payload;
      }
    },
    setConsignmentsParams: (state, action) => {
      state.consignmentsParams = action.payload;
    },
    setConsignmentsSelection: (state, action) => {
      state.consignmentsSelection = action.payload;
    },
    setConsignmentsSelectionV2: (state, action) => {
      state.consignmentsSelectionV2 = action.payload;
    },
    setDeliverySelection: (state, action) => {
      state.deliverySelection = action.payload;
    },
    setShipment: (state, action) => {
      state.shipment = action.payload;
    },
    setShipments: (state, action) => {
      state.shipments = action.payload;
    },
    setShipmentsParams: (state, action) => {
      state.shipmentsParams = { ...action.payload };
    },
    updateConsignment: (state, action) => {
      state.consignment = { ...state.consignment, ...action.payload };
    },
    updateConsignmentFiles: (state, action) => {
      state.consignment = state.consignment || {};
      state.consignment.files = state.consignment.files || { count: 0, results: [] };
      state.consignment.files = { ...state.consignment.files, ...action.payload };
    },
    updateConsignmentForASNValidation: (state, action) => {
      const data = action.payload;
      data.forEach(item => {
        const index = state.consignment.items.findIndex(obj => obj.id === item.id);
        if (index !== -1) {
          state.consignment.items[index] = { ...state.consignment.items[index], ...item };
        }
      });
      state.consignment.can_create_asn = state.consignment.items.every(item =>
        item.asn_validation.every(obj => obj.is_valid),
      );
    },
    updateConsignmentsParams: (state, action) => {
      state.consignmentsParams = { ...state.consignmentsParams, ...action.payload };
    },
    updateShipment: (state, action) => {
      state.shipment = { ...state.shipment, ...action.payload };
    },
    updateShipmentLine: (state, action) => {
      const shipmentLineIndex = state.shipment.shipment_lines.findIndex(
        item => item.id === action.payload.id,
      );
      if (shipmentLineIndex !== -1) {
        state.shipment.shipment_lines[shipmentLineIndex] = action.payload;
      }
      state.shipment.supplier = action.payload.supplier;
    },
    updateShipmentsParams: (state, action) => {
      state.shipmentsParams = { ...state.shipmentsParams, ...action.payload };
    },
    updateTrackingInfo: (state, action) => {
      action.payload.forEach(item => {
        const { id, ...rest } = item;
        const index = state.consignment.items.findIndex(obj => obj.id === id);
        if (index !== -1) {
          state.consignment.items[index] = { ...state.consignment.items[index], ...rest };
        }
      });
    },
    setDeliveries: (state, action) => {
      state.deliveries = action.payload;
    },
    setDelivery: (state, action) => {
      state.delivery = action.payload;
    },
    clearDelivery: state => {
      state.delivery = null;
    },
    updateDeliveryStatusInList: (state, action) => {
      const { id, status } = action.payload;
      const results = state.deliveries?.results;
      if (!Array.isArray(results)) return;
      const index = results.findIndex(item => item.id === id);
      if (index !== -1) {
        state.deliveries.results[index].status = status;
      }
    },
    setDeliveryParams: (state, action) => {
      state.deliveryParams = action.payload;
    },
    updateDeliveryParams: (state, action) => {
      state.deliveryParams = { ...state.deliveryParams, ...action.payload };
    },
    resetDeliveryParams: state => {
      state.deliveryParams = { limit: state.deliveryParams.limit, offset: 0 };
    },
    setDeliveryLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.deliveryLayout = {
        ...state.deliveryLayout,
        listView: {
          ...state.deliveryLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.deliveryLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    setShipmentsV1: (state, action) => {
      state.shipmentsV1 = action.payload;
    },
    setShipmentV1Selection: (state, action) => {
      state.shipmentsV1Selection = action.payload;
    },
    setShipmentV1: (state, action) => {
      state.shipmentV1 = action.payload;
    },
    clearShipmentV1: state => {
      state.shipmentV1 = null;
    },
    updateShipmentV1StatusInList: (state, action) => {
      const { id, status } = action.payload;
      const results = state.shipmentsV1?.results;
      if (!Array.isArray(results)) return;
      const index = results.findIndex(item => item.id === id);
      if (index !== -1) {
        state.shipmentsV1.results[index].status = status;
      }
    },
    setShipmentsV1Params: (state, action) => {
      state.shipmentsV1Params = action.payload;
    },
    updateShipmentsV1Params: (state, action) => {
      state.shipmentsV1Params = { ...state.shipmentsV1Params, ...action.payload };
    },
    resetShipmentsV1Params: state => {
      state.shipmentsV1Params = { limit: state.shipmentsV1Params.limit, offset: 0 };
    },
    setShipmentV1LayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.shipmentsV1Layout = {
        ...state.shipmentsV1Layout,
        listView: {
          ...state.shipmentsV1Layout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.shipmentsV1Layout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    setEquipmentTypeCategories: (state, action) => {
      state.equipmentTypeCategories = action.payload;
    },
    setEquipmentTypes: (state, action) => {
      state.equipmentTypes = action.payload;
    },
    setEquipmentType: (state, action) => {
      state.equipmentType = action.payload;
    },
    setEquipmentTypesSelection: (state, action) => {
      state.equipmentTypesSelection = action.payload;
    },
    clearEquipmentType: state => {
      state.equipmentType = null;
    },
    setEquipmentTypesParams: (state, action) => {
      state.equipmentTypesParams = action.payload;
    },
    updateEquipmentTypesParams: (state, action) => {
      state.equipmentTypesParams = { ...state.equipmentTypesParams, ...action.payload };
    },
    setEquipmentTypeLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.equipmentTypeLayout = {
        ...state.equipmentTypeLayout,
        listView: {
          ...state.equipmentTypeLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.equipmentTypeLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    setShipmentLegs: (state, action) => {
      state.shipmentLegs = action.payload;
    },
    setShipmentLeg: (state, action) => {
      state.shipmentLeg = action.payload;
    },
    updateShipmentLegStatus: (state, action) => {
      const { status } = action.payload;
      state.shipmentLeg = { ...state.shipmentLeg, status };
    },
    clearShipmentLeg: state => {
      state.shipmentLeg = null;
    },
    setShipmentLegsParams: (state, action) => {
      state.shipmentLegsParams = action.payload;
    },
    updateShipmentLegsParams: (state, action) => {
      state.shipmentLegsParams = { ...state.shipmentLegsParams, ...action.payload };
    },
    setShipmentLegLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.shipmentLegsLayout = {
        ...state.shipmentLegsLayout,
        listView: {
          ...state.shipmentLegsLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.shipmentLegsLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    setShipmentLegCarriersSelection: (state, action) => {
      state.shipmentLegCarriersSelection = action.payload;
    },
    setShipmentLegOriginsSelection: (state, action) => {
      state.shipmentLegOriginsSelection = action.payload;
    },
    setShipmentLegDestinationsSelection: (state, action) => {
      state.shipmentLegDestinationsSelection = action.payload;
    },
    setShipmentLegDeliverTosSelection: (state, action) => {
      state.shipmentLegDeliverTosSelection = action.payload;
    },
    setShipmentLegVehicleReferencesSelection: (state, action) => {
      state.shipmentLegVehicleReferencesSelection = action.payload;
    },
    setShipmentLegSequencesSelection: (state, action) => {
      state.shipmentLegSequencesSelection = action.payload;
    },

    setEquipmentLayoutAllViews: (state, action) => {
      const data = action.payload;
      const views = sortAllView(data);
      state.equipmentLayout = {
        ...state.equipmentLayout,
        listView: {
          ...state.equipmentLayout.listView,
          nameOfViews: views.list.nameOfList,
          allViews: { ...state.equipmentLayout.listView.allViews, ...views.list.allViews },
        },
        completeListOfViews: action.payload,
      };
    },
    setEquipments: (state, action) => {
      state.equipments = action.payload;
    },
    setEquipment: (state, action) => {
      state.equipment = action.payload;
    },
    setEquipmentsSelection: (state, action) => {
      state.equipmentsSelection = action.payload;
    },
    clearEquipment: state => {
      state.equipment = null;
    },
    setEquipmentsParams: (state, action) => {
      state.equipmentsParams = action.payload;
    },
    updateEquipmentsParams: (state, action) => {
      state.equipmentsParams = { ...state.equipmentsParams, ...action.payload };
    },
  },
});

export const {
  addConsignmentFiles,
  addConsignmentNote,
  addShipment,
  confirmASN,
  editConsignment,
  editConsignmentItem,
  mutateConsignmentItem,
  mutateShipmentLine,
  removeConsignmentNote,
  resetASN,
  resetASNList,
  resetASNListParams,
  resetConsignment,
  resetConsignmentItems,
  resetConsignmentItemsParams,
  resetConsignments,
  resetConsignmentsParams,
  resetConsignmentsSelection,
  resetShipment,
  resetShipments,
  resetShipmentsParams,
  resetNotesList,
  setASN,
  setASNList,
  setASNListParams,
  updateASNListParams,
  setASNSelection,
  setAsnLayoutAllViews,
  clearASN,
  setCancelConsignments,
  setConsignment,
  setConsignmentBills,
  setConsignmentBillsParams,
  setConsignmentFiles,
  setConsignmentFilesParams,
  setConsignmentItems,
  setConsignmentItemsParams,
  setConsignmentItemsStatus,
  setConsignmentStatus,
  setConsignmentNotes,
  setConsignments,
  setConsignmentsParams,
  setConsignmentsSelection,
  setConsignmentsSelectionV2,
  setShipment,
  setShipments,
  setShipmentsParams,
  updateConsignmentNote,
  updateConsignment,
  updateConsignmentFiles,
  updateConsignmentForASNValidation,
  updateConsignmentsParams,
  updateShipment,
  updateShipmentLine,
  updateShipmentsParams,
  updateTrackingInfo,
  setDeliveries,
  setDelivery,
  clearDelivery,
  setDeliveryParams,
  updateDeliveryParams,
  resetDeliveryParams,
  setDeliveryLayoutAllViews,
  setDeliverySelection,
  updateDeliveryStatusInList,
  setShipmentV1Selection,
  setShipmentsV1,
  setShipmentV1,
  clearShipmentV1,
  setShipmentsV1Params,
  updateShipmentsV1Params,
  resetShipmentsV1Params,
  setShipmentV1LayoutAllViews,
  updateShipmentV1StatusInList,
  setEquipmentTypeCategories,
  setEquipmentTypes,
  setEquipmentType,
  setEquipmentTypesSelection,
  clearEquipmentType,
  setEquipmentTypesParams,
  updateEquipmentTypesParams,
  setEquipmentTypeLayoutAllViews,
  setEquipments,
  setEquipment,
  setEquipmentsSelection,
  clearEquipment,
  setEquipmentsParams,
  updateEquipmentsParams,
  setEquipmentLayoutAllViews,
  setShipmentLegs,
  setShipmentLeg,
  updateShipmentLegStatus,
  clearShipmentLeg,
  setShipmentLegsParams,
  updateShipmentLegsParams,
  setShipmentLegLayoutAllViews,
  setShipmentLegCarriersSelection,
  setShipmentLegOriginsSelection,
  setShipmentLegDestinationsSelection,
  setShipmentLegDeliverTosSelection,
  setShipmentLegVehicleReferencesSelection,
  setShipmentLegSequencesSelection,
} = shipmentSlice.actions;

export default shipmentSlice.reducer;
